import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NumericInput } from '@makemydeal/dr-shared-components/numeric-input';
import { isDifferentLender, toSplitPercent } from '@makemydeal/dr-offer-redux';
import { RESERVE_SPLIT_MAX, RESERVE_SPLIT_MIN } from '@makemydeal/dr-shared-ui-utils/validationConfigs/reserveValidationConfig';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as offerReduxActionCreators from '@makemydeal/dr-dash-store/actions/offerRedux';
import { dealLockHooks } from '../../hooks';
import { RESERVE_SPLIT } from '../../constants';
import ReserveSplitClearedAlert from './ReserveSplitClearedAlert';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

export const RESERVE_SPLIT_TEST_ID = 'reserve-split';

type Props = {
    hasError?: boolean;
    errorMessage?: string;
    onBlur?: (value: number | undefined) => void;
};

const ReserveSplitField = ({ hasError, errorMessage, onBlur }: Props) => {
    const dispatch = useDispatch();

    const override = useSelector(offerReduxSelectors.getReserveSplitOverride);
    const calculated = useSelector(offerReduxSelectors.getReservePaid);
    const lender = useSelector(offerReduxSelectors.getCreditDecisionLender);
    const isLocked = dealLockHooks.useIsDealLocked();

    /**
     * Read-only on a program-quotes deal, because the override cannot reach Payment Services there:
     * `createPaymentOverrides` carries no `reserveSplit`, only `createPaymentOverridesForGenericCalcs` does.
     * Left editable it accepted a value that changed no payment, contradicted the "Rate Reserve at N% split"
     * row below it (that row always reads the Payment Services figure), and then reverted on reload, since the
     * saved `proceeds.reservePaid` is the DRaaS-resolved split rather than the entry.
     *
     * Keyed on the positive program-quotes check, not `!getIsUserProgramQuotes`. An offer type whose endpoint
     * is not resolved yet is neither, and deals initialize that way - negating the user-program-quotes
     * selector is what made the field appear locked on load.
     */
    const isProgramQuotes = useSelector(offerReduxSelectors.getIsProgramQuotes);

    // The override is what the Finance Manager typed; the calculated value is whatever Payment Services last
    // resolved, from the dealer's DRaaS rule or the global default. Showing the override first is what makes an
    // edit survive the payment round-trip instead of being visibly replaced by the recalculated figure - except
    // where it can never be honoured, and the Payment Services figure is the only truthful thing to show.
    const displayedValue = isProgramQuotes ? toSplitPercent(calculated) : override ?? toSplitPercent(calculated);

    const [inputText, setInputText] = useState<number | string>(displayedValue ?? '');
    const [isEdited, setIsEdited] = useState(false);

    // Skipped while an edit is uncommitted so a payment response landing mid-edit cannot replace what the
    // Finance Manager is still typing - including a value being corrected after a range error.
    useEffect(() => {
        if (isEdited) return;
        setInputText(displayedValue ?? '');
    }, [displayedValue, isEdited]);

    const previousLenderRef = useRef(lender);

    useEffect(() => {
        const previousLender = previousLenderRef.current;
        previousLenderRef.current = lender;
        if (!isEdited || !isDifferentLender(previousLender, lender)) return;

        setIsEdited(false);
        onBlur?.(undefined);
    }, [lender, isEdited, onBlur]);

    const handleChange = (e: any) => {
        setInputText(e.target.value);
        setIsEdited(true);
    };

    const handleBlur = () => {
        // Only a real edit may dispatch. The field is seeded with the split Payment Services resolved, so a
        // focus/blur with no keystroke would otherwise post that figure back as a manual override - and it is
        // the mere presence of `reserveSplit` that suppresses the DRaaS Reserve Setup lookup.
        if (!isEdited) return;

        const raw = String(inputText).replace(/,/g, '').trim();
        const parsed = parseFloat(raw);

        // A cleared field dispatches `undefined`, not 0. Only an omitted split restores the DRaaS Reserve Setup
        // lookup; a typed 0 is a real 0% override and is sent like any other value.
        const newValue = raw === '' || isNaN(parsed) ? undefined : parsed;

        // Range enforcement lives in `reserveValidationConfig`; these bounds are its exported constants. The
        // dispatch is withheld on error rather than the keystroke rejected, so the bad value stays visible next
        // to its message - and stays flagged as edited, which keeps the effect above from overwriting it.
        const isValid = newValue === undefined || (newValue >= RESERVE_SPLIT_MIN && newValue <= RESERVE_SPLIT_MAX);

        if (isValid) {
            setIsEdited(false);

            if (newValue !== override) {
                dispatch(offerReduxActionCreators.updateReserveSplitOverride(newValue));
            }
        }

        onBlur?.(newValue);
    };

    return (
        <>
            <NumericInput
                id={RESERVE_SPLIT_TEST_ID}
                name={RESERVE_SPLIT_TEST_ID}
                label={RESERVE_SPLIT}
                inputPrefix="%"
                value={inputText}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isLocked}
                readOnly={isProgramQuotes}
                hasError={hasError}
                errorMessage={errorMessage}
                data-testid={RESERVE_SPLIT_TEST_ID}
                size="sm"
                selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
            />
            <ReserveSplitClearedAlert origin="lenderSlideout" />
        </>
    );
};

export default ReserveSplitField;
